import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, RefreshCw, Wrench, CheckCircle, Shield, BookOpen, Award, Loader2, Package, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useRef } from "react";
import QRCode from "qrcode";
import { usePassportById } from "@/hooks/useProductPassport";
import { useTranslation } from "react-i18next";
import { SparePartLabelPrintMenu } from "@/components/labels/SparePartLabelPrintMenu";
import {
  buildPassportPrintJobsFromPassports,
  resolveSparePartLabelSize,
  useLabelPrinter,
  useLabelPrintingSettings,
} from "@/labels";

export default function RefurbishmentPassport() {
  const { t } = useTranslation("refurb");
  const { t: tPassport, i18n } = useTranslation("passport");
  const navigate = useNavigate();
  const { id } = useParams();
  const { passport, loading, notFound } = usePassportById(id);
  const qrRef = useRef<HTMLCanvasElement>(null);
  const { settings: labelPrintingSettings } = useLabelPrintingSettings();
  const { zebraPrint } = useLabelPrinter();

  const passportPublicUrl = passport ? `${window.location.origin}/passport/${passport.id}` : "";

  useEffect(() => {
    if (qrRef.current && passportPublicUrl) {
      QRCode.toCanvas(qrRef.current, passportPublicUrl, { width: 160, margin: 1 }, (err) => {
        if (err) console.error("QR generation error:", err);
      });
    }
  }, [passportPublicUrl]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  if (notFound || !passport) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 p-6">
        <p className="text-foreground font-medium">{t("passport.noPassport")}</p>
        <p className="text-muted-foreground text-sm text-center max-w-md">{t("passport.passportGenerated")}</p>
        <Button variant="outline" onClick={() => navigate(-1)} className="gap-2">
          <ArrowLeft className="w-4 h-4" />
          {t("passport.goBack")}
        </Button>
      </div>
    );
  }

  const completedDate = new Date(passport.completed_at).toLocaleDateString("en-GB");
  const summary = passport.summary_data || {};
  const isRefurbish = passport.workflow_type === "refurbish";
  const WorkflowIcon = isRefurbish ? RefreshCw : Wrench;
  const workflowLabel = isRefurbish ? t("passport.refurbishmentProcess") : t("passport.repairProcess");

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card border-b border-border px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/work-orders")} className="gap-2 text-primary">
              <ArrowLeft className="w-4 h-4" />
              {t("passport.back")}
            </Button>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-foreground">FixID</span>
              <span className="text-muted-foreground text-sm flex items-center gap-1">
                <WorkflowIcon className="w-3 h-3" />
                {workflowLabel}
              </span>
            </div>
          </div>
          <SparePartLabelPrintMenu
            namespace="passport"
            mode="single"
            variant="default"
            className="bg-refurbish hover:bg-refurbish/90"
            label={t("passport.printQrLabel")}
            onPrintBrowser={() => {
              window.open(`/passport/${passport.id}/label`, "_blank");
            }}
            onPrintZebra={async () => {
              if (!passport) return;
              const size = resolveSparePartLabelSize(labelPrintingSettings.defaultLabelSize);
              const jobs = buildPassportPrintJobsFromPassports(
                [passport],
                {
                  certifiedHeading: tPassport("header.certified"),
                  modelCaption: tPassport("print.model", { defaultValue: "Model" }),
                  categoryCaption: tPassport("print.category", { defaultValue: "Category" }),
                  completedCaption: tPassport("print.completed", { defaultValue: "Completed" }),
                  qualityGradeCaption: tPassport("print.qualityGrade", {
                    defaultValue: "Quality grade",
                  }),
                  serialNumberHeading: tPassport("print.serialNumber", {
                    defaultValue: "Serial number",
                  }),
                  scanHint: tPassport("print.scanHint", {
                    defaultValue: "Scan for product passport",
                  }),
                  locale: i18n.language || "en-GB",
                },
                size,
              );
              await zebraPrint(jobs);
            }}
          />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-refurbish/10 flex items-center justify-center mx-auto mb-4">
            <WorkflowIcon className="w-8 h-8 text-refurbish" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">{t("passport.title")}</h1>
          <p className="text-muted-foreground">
            {isRefurbish ? t("passport.certifiedRefurb") : t("passport.certifiedRepair")}
          </p>
          <p className="text-xs text-muted-foreground mt-2 font-mono">
            {t("passport.serial", { serial: passport.serial_number })}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-card rounded-2xl border border-border p-6">
            <h3 className="font-semibold text-foreground mb-4">{t("passport.productInfo")}</h3>
            <div className="space-y-3">
              <div className="flex justify-between"><span className="text-muted-foreground">{t("passport.brand")}</span><span className="font-medium text-foreground">{passport.brand || "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{t("passport.model")}</span><span className="font-medium text-foreground">{passport.model_number || "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{t("passport.category")}</span><span className="font-medium text-foreground">{passport.category || "—"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{t("passport.completionDate")}</span><span className="font-medium text-foreground">{completedDate}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{t("passport.fixidSerial")}</span><span className="font-medium text-foreground">{passport.serial_number}</span></div>
            </div>
          </div>

          <div className="bg-card rounded-2xl border border-border p-6 flex flex-col items-center justify-center">
            <h3 className="font-semibold text-foreground mb-3">{t("passport.scanToVerify")}</h3>
            <canvas ref={qrRef} width={160} height={160} />
            <p className="text-xs text-muted-foreground mt-3 break-all text-center">{passportPublicUrl}</p>
          </div>
        </div>

        <div className="bg-card rounded-2xl border border-border p-6 mb-8">
          <h3 className="font-semibold text-foreground mb-4">{t("passport.workflowSummary")}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <SummaryStat label={t("passport.stepsCompleted")} value={`${summary.stepsCompleted ?? 0}/${summary.totalSteps ?? 0}`} />
            <SummaryStat label={t("passport.photos")} value={summary.photos ?? 0} />
            <SummaryStat label={t("passport.voiceNotes")} value={summary.voiceNotes ?? 0} />
            <SummaryStat label={t("passport.writtenNotes")} value={summary.writtenNotes ?? 0} />
          </div>
          {passport.quality_grade && (
            <div className="mt-6 bg-refurbish/10 rounded-xl p-4 text-center">
              <p className="text-4xl font-bold text-refurbish mb-1">{t("passport.qualityGrade", { grade: passport.quality_grade })}</p>
              <p className="text-sm text-muted-foreground">{t("passport.qualityAssessment")}</p>
            </div>
          )}
        </div>

        <div className="bg-card rounded-2xl border border-border p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Package className="w-5 h-5 text-refurbish" />
            <h3 className="font-semibold text-foreground">{t("passport.sparePartsUsed")}</h3>
          </div>
          {Array.isArray(summary.partsUsed) && summary.partsUsed.length > 0 ? (
            <div className="divide-y divide-border">
              {summary.partsUsed.map((p: any, idx: number) => (
                <div key={idx} className="flex items-center justify-between py-3">
                  <div className="min-w-0">
                    <p className="font-medium text-foreground truncate">{p.name}</p>
                    {p.part_number && (<p className="text-xs text-muted-foreground font-mono">P/N: {p.part_number}</p>)}
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-sm font-medium text-foreground">×{p.quantity}</p>
                    {Number(p.price) > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {(Number(p.price) * Number(p.quantity || 1)).toFixed(2)} {p.currency || "EUR"}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t("passport.noSparePartsUsed")}</p>
          )}
        </div>

        <div className="bg-card rounded-2xl border border-border p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-foreground">
              {isRefurbish ? t("passport.refurbishedBy") : t("passport.servicedBy")}
            </h3>
          </div>
          <p className="text-foreground font-medium">
            {summary.company || t("passport.independentTechnician")}
          </p>
          <p className="text-xs text-muted-foreground mt-1">{t("passport.performedUnder")}</p>
        </div>

        <div className="bg-card rounded-2xl border border-border p-6 mb-8">
          <h3 className="font-semibold text-foreground mb-4">{t("passport.warrantyCertification")}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="text-center p-4 bg-primary/5 rounded-xl border border-primary/20">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <p className="font-medium text-foreground">{t("passport.warranty")}</p>
              <p className="text-sm text-muted-foreground">{t("passport.partsLabor")}</p>
            </div>
            <div className="text-center p-4 bg-refurbish/5 rounded-xl border border-refurbish/20">
              <div className="w-12 h-12 rounded-full bg-refurbish/10 flex items-center justify-center mx-auto mb-3">
                <BookOpen className="w-6 h-6 text-refurbish" />
              </div>
              <p className="font-medium text-foreground">{t("passport.producerGuidelines")}</p>
              <p className="text-sm text-muted-foreground">{t("passport.certifiedProcess")}</p>
            </div>
            <div className="text-center p-4 bg-refurbish/5 rounded-xl border border-refurbish/20">
              <div className="w-12 h-12 rounded-full bg-refurbish/10 flex items-center justify-center mx-auto mb-3">
                <Award className="w-6 h-6 text-refurbish" />
              </div>
              <p className="font-medium text-foreground">{t("passport.fixidCertified")}</p>
              <p className="text-sm text-muted-foreground">{t("passport.professionalGrade")}</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-xl">
            <CheckCircle className="w-5 h-5 text-refurbish flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-foreground mb-1">{t("passport.fixidPassport")}</p>
              <p className="text-sm text-muted-foreground">{t("passport.passportDesc")}</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function SummaryStat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="text-center p-3 bg-muted/40 rounded-xl">
      <p className="text-2xl font-bold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </div>
  );
}
